import { useConnection } from '@estream/sdk-browser/react';
import { useSettings } from '../context/SettingsContext';
import { useDebug, ALL_CATEGORIES, EventSeverity, EventCategory } from '../context/DebugContext';
import { getSettings, updateSettings } from '../services/LatticeEventsService';
import { useState, useEffect } from 'react';
import './Settings.css';

export default function Settings() {
  const { state, network } = useConnection();
  const { settings, updateSetting } = useSettings();
  const { state: debugState, setLogLevel, setCategories } = useDebug();
  const [latticeEventsEnabled, setLatticeEventsEnabled] = useState(false);
  
  // Load lattice events settings
  useEffect(() => {
    const latticeSettings = getSettings();
    setLatticeEventsEnabled(latticeSettings.enabled);
  }, []);
  
  const handleLogLevelChange = (level: EventSeverity) => {
    setLogLevel(level);
  };
  
  const handleCategoryToggle = (category: EventCategory) => {
    const currentCategories = Array.from(debugState.filter.categories);
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    setCategories(newCategories as EventCategory[]);
  };
  
  const handleLatticeEventsToggle = (enabled: boolean) => {
    setLatticeEventsEnabled(enabled);
    updateSettings({ enabled });
  };
  
  return (
    <div className="settings-page animate-in">
      {/* Connection Settings */}
      <section className="card">
        <h2 className="card-title">Connection</h2>
        
        <div className="settings-grid">
          <div className="setting-item">
            <label className="setting-label">Network</label>
            <div className="setting-value">
              <code>{network}</code>
            </div>
          </div>
          
          <div className="setting-item">
            <label className="setting-label">Edge URL</label>
            <div className="setting-value">
              <code>{import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev:4433'}</code>
            </div>
          </div>
          
          <div className="setting-item">
            <label className="setting-label">Connection Status</label>
            <div className="setting-value">
              <span className={`status-badge ${state}`}>{state}</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Developer Tools */}
      <section className="card">
        <h2 className="card-title">Developer Tools</h2>
        
        <div className="settings-grid">
          <div className="setting-item toggle-setting">
            <div className="setting-info">
              <label className="setting-label">Show CLI Commands</label>
              <p className="setting-description">
                Display equivalent estream-cli commands for console actions. 
                Useful for scripting and automation.
              </p>
            </div>
            <div className="setting-value">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.showCliCommands}
                  onChange={(e) => updateSetting('showCliCommands', e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>
          </div>
        </div>
      </section>
      
      {/* Appearance */}
      <section className="card">
        <h2 className="card-title">Appearance</h2>
        
        <div className="settings-grid">
          <div className="setting-item">
            <label className="setting-label">Theme</label>
            <div className="setting-value">
              <select className="select">
                <option value="dark">Dark</option>
                <option value="light" disabled>Light (coming soon)</option>
              </select>
            </div>
          </div>
        </div>
      </section>
      
      {/* Debug & Instrumentation */}
      <section className="card">
        <h2 className="card-title">Debug & Instrumentation</h2>
        <p className="card-description">
          Configure event logging and debug panel settings. Press <kbd>Ctrl+Shift+D</kbd> to toggle the debug panel.
        </p>
        
        <div className="settings-grid">
          <div className="setting-item">
            <label className="setting-label">Log Level</label>
            <div className="setting-value">
              <select 
                className="select"
                value={debugState.logLevel}
                onChange={(e) => handleLogLevelChange(e.target.value as EventSeverity)}
              >
                <option value="debug">Debug (verbose)</option>
                <option value="info">Info (default)</option>
                <option value="warning">Warning</option>
                <option value="error">Error only</option>
              </select>
            </div>
          </div>
          
          <div className="setting-item toggle-setting">
            <div className="setting-info">
              <label className="setting-label">Live Lattice Events</label>
              <p className="setting-description">
                Subscribe to real-time events from edge proxy via SSE.
              </p>
            </div>
            <div className="setting-value">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={latticeEventsEnabled}
                  onChange={(e) => handleLatticeEventsToggle(e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>
          </div>
          
          <div className="setting-item category-setting">
            <label className="setting-label">Enabled Event Categories</label>
            <div className="category-toggles">
              {ALL_CATEGORIES.map(category => (
                <label key={category} className="category-toggle">
                  <input
                    type="checkbox"
                    checked={debugState.filter.categories.has(category)}
                    onChange={() => handleCategoryToggle(category)}
                  />
                  <span className="category-name">{category}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* About */}
      <section className="card">
        <h2 className="card-title">About</h2>
        
        <div className="about-info">
          <p><strong>eStream Console</strong> v0.5.0</p>
          <p className="text-muted">
            Built with @estream/sdk-browser and React.
          </p>
          <p className="text-muted">
            © 2026 eStream. All rights reserved.
          </p>
        </div>
      </section>
    </div>
  );
}
